import { useState } from 'react';
import { Alert, Button, Input, InputNumber, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { Banknote, FileSpreadsheet, ListChecks, Printer, Scale } from 'lucide-react';
import Layout from '../../Layout';
import usePosStore from '../POS/store';
import { printHtml } from '../Treatment/print';
import { bankTxns, payooTxns, shiftHtml, shiftSheets, shiftSummary } from './ktCounter';
import { dmy, fmtMoney, PAY_METHOD, pendingTxns, type Shift, type ShiftTxn } from './ktLogic';
import { ktApi, today, useKt } from './ktStore';
import { CounterBar, DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';

const money = (v: number | string | undefined) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const unmoney = (v: string | undefined) => Number((v || '').replace(/\./g, ''));
const SHIFTS = ['Ca sáng', 'Ca chiều', 'Ca tối'];
export const STATUS: Record<Shift['status'], { label: string; color: string }> = {
  closed: { label: 'Chờ Kế toán nội bộ nhận', color: 'orange' }, received: { label: 'Đã nhận', color: 'green' }, reopened: { label: 'Đã mở lại', color: 'default' },
};

export const txnColumns: ColumnsType<ShiftTxn> = [
  { title: 'Số bill', dataIndex: 'billNo', width: 110 },
  { title: 'Khách', dataIndex: 'customer' },
  { title: 'Hình thức', key: 'm', width: 150, render: (_, t) => `${PAY_METHOD[t.method]}${t.kind === 'refund' ? ' (hoàn)' : ''}` },
  { title: 'Số tiền', dataIndex: 'amount', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
  { title: 'Mã giao dịch', key: 'r', width: 170, render: (_, t) => t.ref || <span className="text-gray-400">Không có</span> },
];

export function exportShift(x: Shift) {
  const wb = XLSX.utils.book_new();
  shiftSheets(x).forEach(sheet => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet.rows), sheet.name));
  XLSX.writeFile(wb, `${x.no}_${x.date}.xlsx`);
}

/** Chốt ca: bắt buộc kiểm kê tiền mặt, tự xuất biên bản và bảng kê gửi Kế toán bán hàng (SRS 3.3) */
export default function KeToanShift() {
  const s = useKt();
  const now = today();
  const pending = pendingTxns(s);
  const t = shiftSummary(pending);
  const posClose = usePosStore(p => p.shiftCloses).find(c => c.at.slice(0, 10) === now && c.branchId === 'q1');

  const [name, setName] = useState(dayjs().hour() < 14 ? SHIFTS[0] : SHIFTS[1]);
  const [opening, setOpening] = useState(s.cashFloat);
  const [counted, setCounted] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const expected = opening + t.cash;
  const diff = counted === null ? 0 : counted - expected;
  const waiting = s.shifts.filter(x => x.status === 'closed');

  const close = () => {
    const r = ktApi.closeShift({ name, openingCash: opening, cashCounted: counted, diffReason: reason });
    if (r.ok) { message.success('Đã chốt ca, biên bản và bảng kê đã gửi Kế toán nội bộ'); setCounted(null); setReason(''); } else showErrors(r.errors);
  };

  const historyColumns: ColumnsType<Shift> = [
    { title: 'Số ca', dataIndex: 'no', width: 100 },
    { title: 'Ca', key: 'n', width: 150, render: (_, x) => <div><div>{x.name}</div><div className="text-[11px] text-gray-500">{dmy(x.date)}, chốt lúc {dayjs(x.at).format('HH:mm')}</div></div> },
    { title: 'Người chốt', dataIndex: 'by', width: 180 },
    { title: 'Tiền mặt theo sổ', dataIndex: 'cashExpected', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Kiểm kê', dataIndex: 'cashCounted', width: 130, align: 'right', render: (v: number) => fmtMoney(v) },
    { title: 'Chênh lệch', key: 'd', width: 200, render: (_, x) => (x.diff ? <div><b className="text-rose-600">{fmtMoney(x.diff)}</b><div className="text-[11px] text-gray-500">{x.diffReason}</div></div> : <span className="text-emerald-700">Khớp</span>) },
    { title: 'Trạng thái', key: 's', width: 180, render: (_, x) => <div><Tag color={STATUS[x.status].color}>{STATUS[x.status].label}</Tag>{x.reopenReason && <div className="text-[11px] text-gray-500">{x.reopenedBy}: {x.reopenReason}</div>}</div> },
    { title: '', key: 'a', width: 90, align: 'right', render: (_, x) => (
      <div className="flex justify-end gap-1">
        <Tooltip title="In biên bản và bảng kê"><Button size="small" type="text" icon={<Printer size={14} />} aria-label={`In ${x.no}`} onClick={() => printHtml(`Biên bản chốt ca ${x.no}`, 'A4', shiftHtml(x))} /></Tooltip>
        <Tooltip title="Xuất Excel"><Button size="small" type="text" icon={<FileSpreadsheet size={14} />} aria-label={`Excel ${x.no}`} onClick={() => exportShift(x)} /></Tooltip>
      </div>
    ) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Chốt ca" desc="Đếm tiền mặt thực tế từng ca. Chốt xong hệ thống tự lập biên bản và bảng kê Ngân hàng, Payoo gửi Kế toán bán hàng." />
        <DemoBar portal="counter" />
        <CounterBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<ListChecks size={20} />} tone="bg-sky-50 text-sky-600" label="Giao dịch chưa chốt" value={pending.length} hint={fmtMoney(t.total)} />
          <Kpi icon={<Banknote size={20} />} tone="bg-emerald-50 text-emerald-600" label="Tiền mặt theo sổ" value={fmtMoney(expected)} hint={`Quỹ đầu ca ${fmtMoney(opening)}`} />
          <Kpi icon={<Scale size={20} />} tone="bg-amber-50 text-amber-600" label="Ngân hàng, Payoo" value={fmtMoney(bankTxns(pending).concat(payooTxns(pending)).reduce((a, x) => a + x.amount, 0))} hint="Sẽ vào bảng kê" />
          <Kpi icon={<FileSpreadsheet size={20} />} tone="bg-rose-50 text-rose-600" label="Biên bản chờ nhận" value={waiting.length} hint="Kế toán nội bộ nhận trước khi khóa sổ" />
        </div>

        <Panel title="Ca hiện tại">
          <div className="px-4 py-3 space-y-3">
            {t.missing.length > 0 && <Alert type="warning" showIcon message={`${t.missing.length} giao dịch thiếu mã: vẫn chốt ca được, nhưng sổ ngày không khóa được cho tới khi bổ sung.`} />}
            {posClose && <Alert type="info" showIcon message={`POS đã chốt ca ${posClose.id} lúc ${dayjs(posClose.at).format('HH:mm')}, đếm tiền mặt ${fmtMoney(posClose.counted.cash)}. POS và Kế toán đang chốt riêng, đối chiếu số này khi kiểm kê.`} />}
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
              <div className="space-y-3">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm space-y-0.5">
                  {t.rows.map(r => <div key={r.method} className="flex justify-between"><span className="text-gray-500">{r.label} ({r.count})</span><span>{fmtMoney(r.amount)}</span></div>)}
                  {!t.rows.length && <div className="text-gray-500">Không có giao dịch chưa chốt</div>}
                  {t.rows.length > 0 && <div className="flex justify-between font-semibold border-t border-gray-200 dark:border-gray-700 pt-1 mt-1"><span>Tổng</span><span>{fmtMoney(t.total)}</span></div>}
                </div>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label htmlFor="kt-sh-name" className="text-sm font-medium">Ca</label><Select id="kt-sh-name" className="w-full" value={name} onChange={setName} options={SHIFTS.map(x => ({ value: x, label: x }))} /></div>
                      <div><label htmlFor="kt-sh-open" className="text-sm font-medium">Quỹ đầu ca</label><InputNumber id="kt-sh-open" className="!w-full" min={0} step={100000} value={opening} onChange={v => setOpening(Number(v) || 0)} formatter={money} parser={unmoney} /></div>
                    </div>
                    <div><label htmlFor="kt-sh-count" className="text-sm font-medium">Tiền mặt kiểm kê thực tế (bắt buộc)</label><InputNumber id="kt-sh-count" className="!w-full" min={0} step={100000} value={counted} onChange={v => setCounted(v === null ? null : Number(v))} formatter={money} parser={unmoney} /></div>
                    {counted !== null && <div className={`text-sm font-medium ${diff ? 'text-rose-600' : 'text-emerald-700'}`}>{diff ? `Lệch ${fmtMoney(diff)} so với sổ ${fmtMoney(expected)}` : 'Khớp với sổ'}</div>}
                    {diff !== 0 && <div><label htmlFor="kt-sh-reason" className="text-sm font-medium">Lý do chênh lệch</label><Input.TextArea id="kt-sh-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} /></div>}
                    <Button type="primary" block disabled={!pending.length || counted === null} onClick={close}>Chốt ca, gửi Kế toán bán hàng</Button>
                </div>
              </div>
              <Table rowKey="id" size="small" columns={txnColumns} dataSource={pending} pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 700 }} locale={{ emptyText: 'Không có giao dịch chưa chốt' }} />
            </div>
          </div>
        </Panel>

        <Panel title={`Lịch sử chốt ca (${s.shifts.length})`}>
          <Table rowKey="id" size="middle" columns={historyColumns} dataSource={s.shifts} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 1250 }} locale={{ emptyText: 'Chưa có ca nào được chốt' }}
            expandable={{ expandedRowRender: x => <Table rowKey="id" size="small" columns={txnColumns} dataSource={x.txns} pagination={false} /> }} />
        </Panel>
      </div>

    </Layout>
  );
}
